import { useCallback, useEffect, useRef, useState } from 'react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import { simpleSelfieCopy } from '@/Pages/Selfie/copy'
import {
  acquireCameraStream,
  CAMERA_TIMINGS,
  cameraErrorName,
  nextCameraRecovery,
  playVideo,
  reloadPage,
  startCameraWatchdog,
  stopMediaStream,
  wait,
} from '@/Pages/Selfie/helpers/cameraAccess'
import type { CameraRestartInfo, CameraRestartReason } from '@/Pages/Selfie/helpers/cameraAccess'
import {
  captureVideoFrameAsDataUrl,
  isVideoFrameReady,
} from '@/Pages/Selfie/helpers/captureVideoFrame'
import { readUploadedImageAsDataUrl } from '@/Pages/Selfie/helpers/uploadImageFile'
import '@/Pages/Selfie/components/SimpleSelfieCapture.sass'

type Props = {
  onAnalysisComplete: (base64Image: string) => void
  /** A câmera travou ou falhou ao abrir e está sendo reaberta (rastro no painel). */
  onCameraRestart: (info: CameraRestartInfo) => void
}

export function SimpleSelfieCapture({ onAnalysisComplete, onCameraRestart }: Props) {
  const videoRef = useRef<HTMLVideoElement>(null)
  const uploadInputRef = useRef<HTMLInputElement>(null)
  const onCameraRestartRef = useRef(onCameraRestart)
  onCameraRestartRef.current = onCameraRestart
  const [cameraReady, setCameraReady] = useState(false)
  const [cameraRecovering, setCameraRecovering] = useState(false)
  const [cameraUnavailable, setCameraUnavailable] = useState(false)
  const [uploadError, setUploadError] = useState(false)

  // Mesma defesa do modo liveness (ver helpers/cameraAccess): repete o erro
  // passageiro, vigia o vídeo até o primeiro quadro e reabre se travar; por
  // último recarrega a página uma vez. Só então cai no "câmera indisponível",
  // que ainda deixa enviar uma foto.
  useEffect(() => {
    let disposed = false
    let run = 0
    let restarts = 0
    let stream: MediaStream | null = null
    let stopWatchdog: (() => void) | undefined

    const release = () => {
      stopWatchdog?.()
      stopWatchdog = undefined
      if (stream !== null) stopMediaStream(stream)
      stream = null
      if (videoRef.current) videoRef.current.srcObject = null
    }

    const open = async (recovering: boolean) => {
      const current = ++run
      const isStale = () => disposed || current !== run
      release()
      setCameraReady(false)
      setCameraRecovering(recovering)
      if (recovering) await wait(CAMERA_TIMINGS.releaseMs)
      if (isStale()) return

      const result = await acquireCameraStream(isStale)
      if (isStale()) {
        if (result.ok) stopMediaStream(result.stream)
        return
      }
      if (!result.ok) {
        if (result.kind === 'transient') {
          recover('warmup_failed', result.error)
        } else {
          setCameraRecovering(false)
          setCameraUnavailable(true)
        }
        return
      }

      stream = result.stream
      const video = videoRef.current
      if (video) {
        video.srcObject = stream
        playVideo(video)
      }
      stopWatchdog = startCameraWatchdog({
        getVideo: () => videoRef.current,
        onStall: (reason) => {
          recover(reason)
        },
      })
    }

    const recover = (reason: CameraRestartReason, error?: unknown) => {
      if (disposed) return
      const errorName = cameraErrorName(error)
      const next = nextCameraRecovery(restarts)
      if (next === 'restart') {
        restarts += 1
        onCameraRestartRef.current({ reason, attempt: restarts, errorName, reloading: false })
        void open(true)
        return
      }
      run += 1
      release()
      if (next === 'reload') {
        onCameraRestartRef.current({ reason, attempt: restarts + 1, errorName, reloading: true })
        reloadPage()
        return
      }
      setCameraRecovering(false)
      setCameraUnavailable(true)
    }

    void open(false)

    return () => {
      disposed = true
      release()
    }
  }, [])

  const handleVideoCanPlay = useCallback(() => {
    setCameraReady(true)
    setCameraRecovering(false)
  }, [])

  const handleCapture = useCallback(() => {
    const video = videoRef.current
    if (!video || !isVideoFrameReady(video)) return
    // LGPD: base64 forwarded directly to the callback — never stored in state or storage
    onAnalysisComplete(captureVideoFrameAsDataUrl(video))
  }, [onAnalysisComplete])

  const handleUploadClick = () => {
    setUploadError(false)
    uploadInputRef.current?.click()
  }

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    e.target.value = ''
    try {
      // LGPD: base64 forwarded directly to the callback — never stored in state or storage
      onAnalysisComplete(await readUploadedImageAsDataUrl(file))
    } catch {
      setUploadError(true)
    }
  }

  return (
    <Box className="simple-selfie-capture">
      {cameraUnavailable ? (
        <Box
          className="simple-selfie-capture__camera-unavailable"
          data-testid="simple-selfie-camera-unavailable"
        >
          <Typography component="h2" className="simple-selfie-capture__unavailable-title">
            {simpleSelfieCopy.cameraUnavailableTitle}
          </Typography>
          <Typography className="simple-selfie-capture__unavailable-description">
            {simpleSelfieCopy.cameraUnavailableDescription}
          </Typography>
        </Box>
      ) : (
        <Box className="simple-selfie-capture__camera-container">
          <video
            ref={videoRef}
            className="simple-selfie-capture__video"
            autoPlay
            playsInline
            muted
            onCanPlay={handleVideoCanPlay}
            data-testid="simple-selfie-video"
          />
          {cameraRecovering && (
            <Typography
              className="simple-selfie-capture__recovering"
              role="status"
              data-testid="simple-selfie-camera-recovering"
            >
              {simpleSelfieCopy.cameraRecovering}
            </Typography>
          )}
        </Box>
      )}

      {!cameraUnavailable && (
        <Box className="selfie-capture-controls">
          <button
            type="button"
            className="selfie-capture-controls__button"
            aria-label={simpleSelfieCopy.captureHint}
            data-testid="simple-selfie-capture-button"
            onClick={handleCapture}
            disabled={!cameraReady}
          >
            <span className="selfie-capture-controls__button-inner" aria-hidden />
          </button>
          <Typography component="p" className="selfie-capture-controls__hint">
            {simpleSelfieCopy.captureHint}
          </Typography>
        </Box>
      )}

      {uploadError && (
        <Typography
          className="simple-selfie-capture__upload-error"
          role="alert"
          data-testid="simple-selfie-upload-error"
        >
          {simpleSelfieCopy.uploadError}
        </Typography>
      )}

      <input
        ref={uploadInputRef}
        type="file"
        accept="image/*"
        aria-hidden
        tabIndex={-1}
        style={{ display: 'none' }}
        data-testid="simple-selfie-file-input"
        onChange={handleFileChange}
      />

      <Button
        variant={cameraUnavailable ? 'contained' : 'outlined'}
        size="large"
        className={
          cameraUnavailable
            ? 'simple-selfie-capture__upload-button simple-selfie-capture__upload-button--primary'
            : 'simple-selfie-capture__upload-button'
        }
        onClick={handleUploadClick}
        data-testid="simple-selfie-upload-button"
      >
        {simpleSelfieCopy.uploadButton}
      </Button>
    </Box>
  )
}
